import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RefreshCw,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  MoreHorizontal,
  Store,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  useConnectorConfigs,
  useSyncProductToPlatforms,
  useProductSyncStatus,
} from '@/hooks/useConnectorManagement';
import { logError } from '@/utils/errorHandler';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';

interface ProductSyncButtonsProps {
  productId: number;
  productName?: string;
  className?: string;
}

interface SyncResult {
  platform: string;
  success: boolean;
  message: string;
  external_id?: string;
}

export function ProductSyncButtons({ productId, productName, className }: ProductSyncButtonsProps) {
  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [syncResults, setSyncResults] = useState<SyncResult[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

  // Hooks
  const { data: configs, isLoading: configsLoading } = useConnectorConfigs();
  const syncMutation = useSyncProductToPlatforms();
  const syncStatusMutation = useProductSyncStatus();
  const { getPlatformSyncLabels } = useProductDetailPageTranslations();

  // Filter enabled platforms
  const enabledConfigs = configs?.filter(config => config.is_enabled) || [];

  // Handle sync to selected platforms
  const handleSync = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform to sync to');
      return;
    }

    try {
      const result = await syncMutation.mutateAsync({
        productId,
        platforms: selectedPlatforms,
      });

      // Convert results to display format
      const displayResults: SyncResult[] = selectedPlatforms.map(platform => ({
        platform,
        success: result.results[platform]?.success || false,
        message: result.results[platform]?.message || 'Unknown error',
        external_id: result.results[platform]?.external_id,
      }));

      setSyncResults(displayResults);

      // Show summary toast
      const successCount = displayResults.filter(r => r.success).length;
      const failCount = displayResults.length - successCount;

      if (successCount > 0 && failCount === 0) {
        toast.success(`Successfully synced to ${successCount} platform(s)`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Synced to ${successCount} platform(s), ${failCount} failed`);
      } else {
        toast.error(`Failed to sync to all ${failCount} platform(s)`);
      }
    } catch (error: any) {
      toast.error('Failed to sync product');
      logError(error, {
        component: 'ProductSyncButtons',
        action: 'syncProduct',
        metadata: { productId, platforms: selectedPlatforms },
      });
    }
  };

  // Handle sync status check
  const handleCheckStatus = async (platform: string) => {
    try {
      const status = await syncStatusMutation.mutateAsync({
        productId,
        platform,
      });

      // Show status in a toast or dialog
      toast.info(`Sync Status for ${platform}: ${status.is_synced ? 'Synced' : 'Not synced'}`);
    } catch (error: any) {
      toast.error('Failed to check sync status');
      logError(error, {
        component: 'ProductSyncButtons',
        action: 'checkSyncStatus',
        metadata: { productId, platform },
      });
    }
  };

  // Handle platform toggle
  const togglePlatform = (platform: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
    );
  };

  if (configsLoading) {
    return (
      <Card className={className}>
        <CardContent className="p-4">
          <div className="flex items-center gap-2">
            <RefreshCw className="size-4 animate-spin" />
            <span className="text-sm text-muted-foreground">
              {getPlatformSyncLabels.loadingPlatforms}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!enabledConfigs.length) {
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Store className="size-4" />
            {getPlatformSyncLabels.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <p className="text-sm text-muted-foreground">
            {getPlatformSyncLabels.noPlatformsConfigured}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Store className="size-4" />
            {getPlatformSyncLabels.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {/* Platform status badges */}
          <div className="flex flex-wrap gap-2">
            {enabledConfigs.map(config => (
              <Badge key={config.id} variant="outline" className="flex items-center gap-1 text-xs">
                <span>{config.platform}</span>
                {config.auto_sync_enabled && <Clock className="size-3 text-blue-500" />}
              </Badge>
            ))}
          </div>

          {/* Sync buttons */}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => setShowSyncDialog(true)}
              disabled={syncMutation.isPending}
              className="flex-1"
            >
              <Zap className="mr-2 size-4" />
              {syncMutation.isPending
                ? getPlatformSyncLabels.syncing
                : getPlatformSyncLabels.syncNow}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {enabledConfigs.map(config => (
                  <DropdownMenuItem
                    key={config.id}
                    onClick={() => handleCheckStatus(config.platform)}
                  >
                    <ExternalLink className="mr-2 size-4" />
                    {getPlatformSyncLabels.checkStatus.replace(
                      '{{platform}}',
                      getPlatformSyncLabels.platforms[
                        config.platform as keyof typeof getPlatformSyncLabels.platforms
                      ] || config.platform
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Sync Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{getPlatformSyncLabels.syncProduct}</DialogTitle>
            <DialogDescription>
              {getPlatformSyncLabels.selectPlatformsDescription.replace(
                '{{productName}}',
                productName || `Product ${productId}`
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Platform selection */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium">{getPlatformSyncLabels.selectPlatforms}</h4>
              <div className="space-y-2">
                {enabledConfigs.map(config => (
                  <div key={config.id} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`platform-${config.id}`}
                      checked={selectedPlatforms.includes(config.platform)}
                      onChange={() => togglePlatform(config.platform)}
                      className="rounded border-gray-300"
                    />
                    <label
                      htmlFor={`platform-${config.id}`}
                      className="flex flex-1 items-center gap-2 text-sm"
                    >
                      <span className="capitalize">{config.platform}</span>
                      <Badge variant="outline" className="text-xs">
                        {config.name}
                      </Badge>
                      {config.auto_sync_enabled && (
                        <Badge variant="outline" className="text-xs text-blue-600">
                          <Clock className="mr-1 size-3" />
                          Auto: {config.sync_interval}
                        </Badge>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Sync results */}
            {syncResults.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium">{getPlatformSyncLabels.syncResults}</h4>
                <div className="max-h-32 space-y-2 overflow-y-auto">
                  {syncResults.map(result => (
                    <div
                      key={result.platform}
                      className="flex items-center justify-between rounded border p-2 text-sm"
                    >
                      <div className="flex items-center gap-2">
                        {result.success ? (
                          <CheckCircle className="size-4 text-green-600" />
                        ) : (
                          <XCircle className="size-4 text-red-600" />
                        )}
                        <span className="capitalize">{result.platform}</span>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {result.external_id && <span>ID: {result.external_id}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSyncDialog(false);
                  setSyncResults([]);
                  setSelectedPlatforms([]);
                }}
              >
                {getPlatformSyncLabels.close}
              </Button>
              <Button
                onClick={handleSync}
                disabled={syncMutation.isPending || selectedPlatforms.length === 0}
              >
                {syncMutation.isPending ? (
                  <>
                    <RefreshCw className="mr-2 size-4 animate-spin" />
                    {getPlatformSyncLabels.syncing}
                  </>
                ) : (
                  <>
                    <Zap className="mr-2 size-4" />
                    {getPlatformSyncLabels.syncToPlatforms.replace(
                      '{{count}}',
                      selectedPlatforms.length.toString()
                    )}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
